import React, { useState, useRef, useEffect, useCallback } from 'react';
import { ChevronDown } from 'lucide-react';
import './CustomSelect.css';

/**
 * Custom Select component with consistent styling across browsers
 * @param {string} id - Input id
 * @param {string} name - Input name
 * @param {string} value - Selected value
 * @param {function} onChange - Change handler
 * @param {function} onBlur - Blur handler
 * @param {array} options - Array of {value, label} objects
 * @param {string} className - Additional CSS classes
 * @param {boolean} disabled - Disabled state
 */
const CustomSelect = ({
    id,
    name,
    value,
    onChange,
    onBlur,
    options = [],
    className = '',
    disabled = false
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const containerRef = useRef(null);
    const optionsRef = useRef([]);

    // Find selected option
    // Use loose equality to handle string/number mismatches (e.g., '0' vs 0)
    const selectedOption = options.find(opt => opt.value == value) || options[0];

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setIsOpen(false);
                setHighlightedIndex(-1);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Keyboard navigation
    useEffect(() => {
        if (!isOpen) return;

        const handleKeyDown = (e) => {
            switch (e.key) {
                case 'ArrowDown':
                    e.preventDefault();
                    setHighlightedIndex(prev =>
                        prev < options.length - 1 ? prev + 1 : prev
                    );
                    break;
                case 'ArrowUp':
                    e.preventDefault();
                    setHighlightedIndex(prev => prev > 0 ? prev - 1 : 0);
                    break;
                case 'Enter':
                    e.preventDefault();
                    if (highlightedIndex >= 0 && highlightedIndex < options.length) {
                        handleOptionClick(options[highlightedIndex]);
                    }
                    break;
                case 'Escape':
                    e.preventDefault();
                    setIsOpen(false);
                    setHighlightedIndex(-1);
                    break;
                default:
                    break;
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, highlightedIndex, options]);

    // Scroll highlighted option into view
    useEffect(() => {
        if (highlightedIndex >= 0 && optionsRef.current[highlightedIndex]) {
            optionsRef.current[highlightedIndex].scrollIntoView({
                block: 'nearest',
                behavior: 'smooth'
            });
        }
    }, [highlightedIndex]);

    const handleOptionClick = useCallback((option) => {
        // Create synthetic event
        const syntheticEvent = {
            target: {
                name: name,
                value: option.value
            }
        };

        onChange(syntheticEvent);
        setIsOpen(false);
        setHighlightedIndex(-1);

        if (onBlur) {
            onBlur();
        }
    }, [name, onChange, onBlur]);

    const handleToggle = () => {
        if (!disabled) {
            setIsOpen(!isOpen);
            if (!isOpen) {
                // Set highlighted to current selected
                const currentIndex = options.findIndex(opt => opt.value == value);
                setHighlightedIndex(currentIndex >= 0 ? currentIndex : 0);
            }
        }
    };

    return (
        <div
            ref={containerRef}
            className={`custom-select ${isOpen ? 'open' : ''} ${disabled ? 'disabled' : ''} ${className}`}
        >
            <div
                className="custom-select-trigger"
                onClick={handleToggle}
                tabIndex={disabled ? -1 : 0}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleToggle();
                    }
                }}
            >
                <span className="custom-select-value">
                    {selectedOption ? selectedOption.label : 'Select...'}
                </span>
                <ChevronDown
                    size={20}
                    className="custom-select-arrow"
                />
            </div>

            {isOpen && (
                <div className="custom-select-dropdown">
                    {options.map((option, index) => (
                        <div
                            key={option.value}
                            ref={el => optionsRef.current[index] = el}
                            className={`custom-select-option
                                ${option.value == value ? 'selected' : ''}
                                ${index === highlightedIndex ? 'highlighted' : ''}`}
                            onClick={() => handleOptionClick(option)}
                        >
                            {option.label}
                        </div>
                    ))}
                </div>
            )}

            {/* Hidden native select for form compatibility */}
            <select
                id={id}
                name={name}
                value={value}
                onChange={onChange}
                onBlur={onBlur}
                disabled={disabled}
                tabIndex={-1}
                style={{ display: 'none' }}
            >
                {options.map(option => (
                    <option key={option.value} value={option.value}>
                        {option.label}
                    </option>
                ))}
            </select>
        </div>
    );
};

export default CustomSelect;
